import React from "react";
import LanguageSelector from "./LanguageSelector";
import { LanguageCode, translations } from "../utils/translations";
import GoogleLoginButton from "./Auth/GoogleLoginButton";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { COLORS, navLinkStyle } from "../utils/constants";
import { formatPoints } from "../utils/pointsFormatter";
import { searchIndex } from "../utils/searchIndex";
import { getServices } from "../features/services/servicesData";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";

interface NavbarProps {
  currentLang: LanguageCode;
  onLanguageChange: (code: LanguageCode) => void;
  solutionsText: string;
  methodologyText: string;
  aboutText: string;
  dashBoardText: string;
  points?: number;
  pointsLabel?: string;
  ctaText: string;
  contactText: string;
  socialMediaPerformanceText?: string;
  automationNavText?: string;
  blogsText?: string;
  productsText?: string;
  fieldServicePlannerText?: string;
  servicesText?: string;
  seeAllServicesText?: string;
  searchPlaceholderText?: string;
}

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const Navbar: React.FC<NavbarProps> = ({
  currentLang,
  onLanguageChange,
  solutionsText,
  methodologyText,
  aboutText,
  dashBoardText,
  points,
  pointsLabel,
  ctaText,
  contactText,
  socialMediaPerformanceText,
  automationNavText,
  blogsText,
  productsText,
  fieldServicePlannerText,
  servicesText,
  seeAllServicesText,
  searchPlaceholderText,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate(); // ✅ ADDED
  const location = useLocation();

  const [isProductsOpen, setIsProductsOpen] = React.useState(false);
  const productsRef = React.useRef<HTMLDivElement>(null);

  const [isServicesOpen, setIsServicesOpen] = React.useState(false);
  const servicesRef = React.useRef<HTMLDivElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  /* -----------------------------
     GLOBAL SEARCH STATE
  ----------------------------- */

  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<typeof searchIndex>([]);

  React.useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      return;
    }

    const filtered = searchIndex.filter((item) =>
      item.label.toLowerCase().includes(query.toLowerCase())
    );

    setResults(filtered.slice(0, 6));
  }, [query]);

  /* -----------------------------
     CLOSE DROPDOWN OUTSIDE CLICK
  ----------------------------- */

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productsRef.current &&
        !productsRef.current.contains(event.target as Node)
      ) {
        setIsProductsOpen(false);
      }
      if (
        servicesRef.current &&
        !servicesRef.current.contains(event.target as Node)
      ) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className="site-nav"
      aria-label="Main navigation"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.8rem 6%",
        backgroundColor: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 9999, // 🔥 High z-index to prevent overlapping
        borderBottom: "1px solid #f0f0f0",
        pointerEvents: "auto", // Ensure clicks are caught
      }}
    >
      {/* BRAND */}
      <Link to="/" style={{ textDecoration: "none" }}>
        <div
          style={{
            fontSize: "1.3rem",
            fontWeight: 900,
            color: COLORS.brandPurple,
            letterSpacing: "-1px",
          }}
        >
          SOCIAL<span style={{ color: COLORS.brandTeal }}>FLOW</span>
        </div>
      </Link>

      <button
        type="button"
        className="site-nav__toggle"
        aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={isMobileMenuOpen}
        aria-controls="site-navigation-links"
        onClick={() => setIsMobileMenuOpen((open) => !open)}
      >
        {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* NAV LINKS */}
      <div
        id="site-navigation-links"
        className={`site-nav__links${isMobileMenuOpen ? " site-nav__links--open" : ""}`}
        style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
      >
        <Link
          to="/social"
          style={{
            ...navLinkStyle,
            color: COLORS.brandPurple,
            fontWeight: 700, // Slightly bolder for better visibility
            textDecoration: "none",
          }}
        >
          {socialMediaPerformanceText || "SocialMedia Performance"}
        </Link>

        <Link
          to="/automate-business"
          style={{
            ...navLinkStyle,
            color: COLORS.brandPurple,
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          {automationNavText || "Automation"}
        </Link>

        <Link
          to="/blogs"
          style={{
            ...navLinkStyle,
            color: COLORS.brandPurple,
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          {blogsText || "Blogs"}
        </Link>

        <div className="site-nav__dropdown site-nav__dropdown--products" style={{ position: "relative" }} ref={productsRef}>
          <span
            onClick={() => setIsProductsOpen(!isProductsOpen)}
            style={{
              ...navLinkStyle,
              color: COLORS.brandPurple,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {(productsText || "Products")} ▾
          </span>
          {isProductsOpen && (
            <div
              style={{
                position: "absolute",
                top: "120%",
                left: 0,
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                width: "200px",
                zIndex: 500,
                display: "flex",
                flexDirection: "column",
                border: "1px solid #f0f0f0",
              }}
            >
              <Link
                to="/products/field-service-planner"
                onClick={() => setIsProductsOpen(false)}
                style={{
                  padding: "12px 16px",
                  color: "#333",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                {fieldServicePlannerText || "Field Service Planner"}
              </Link>
            </div>
          )}
        </div>

        {/* SERVICES DROPDOWN */}
        <div className="site-nav__dropdown site-nav__dropdown--services" ref={servicesRef} style={{ position: "relative" }}>
          <span
            onClick={() => setIsServicesOpen((v) => !v)}
            style={{
              ...navLinkStyle,
              color: COLORS.brandPurple,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {(servicesText || "Services")} {isServicesOpen ? "▴" : "▾"}
          </span>

          {isServicesOpen && (
            <div
              style={{
                position: "absolute",
                top: "140%",
                right: 0,
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 16px 40px rgba(15,23,42,0.14)",
                border: "1px solid #eef2f7",
                padding: "12px",
                width: "460px",
                maxWidth: "90vw",
                zIndex: 500,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "4px",
                }}
              >
                {getServices(currentLang).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setIsServicesOpen(false)}
                    style={{
                      display: "block",
                      padding: "9px 10px",
                      borderRadius: "8px",
                      textDecoration: "none",
                      color: "#0F172A",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span style={{ fontWeight: 700, display: "block" }}>
                      {s.title}
                    </span>
                    <span style={{ color: "#64748B", fontSize: "0.75rem" }}>
                      {s.group}
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                to="/services"
                onClick={() => setIsServicesOpen(false)}
                style={{
                  display: "block",
                  marginTop: "8px",
                  paddingTop: "10px",
                  borderTop: "1px solid #eef2f7",
                  color: COLORS.brandTeal,
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                }}
              >
                {seeAllServicesText || "See all end-to-end services →"}
              </Link>
            </div>
          )}
        </div>

        {/* -----------------------------
            GLOBAL SEARCH INPUT
        ----------------------------- */}

        <div className="site-nav__search" style={{ position: "relative" }}>
          <input
            type="text"
            placeholder={searchPlaceholderText || "Search..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "0.85rem",
              width: "180px",
            }}
          />

          {results.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                left: 0,
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                width: "260px",
                zIndex: 500,
              }}
            >
              {results.map((item) => (
                <div
                  key={item.id}
                  style={{
                    padding: "10px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  onClick={() => {
                    navigate(item.route); // ✅ NAVIGATE
                    setQuery("");
                    setResults([]);
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{item.label}</div>
                  <div style={{ fontSize: "0.75rem", color: "#777" }}>
                    {item.path.join(" → ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {user && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Link
              to="/dashboard"
              style={{
                ...navLinkStyle,
                color: COLORS.brandPurple,
                padding: "0",
                margin: "0",
              }}
            >
              {dashBoardText}
            </Link>
            {points !== undefined && (
              <Link
                to="/points"
                style={{
                  ...navLinkStyle,
                  color: COLORS.brandTeal,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  margin: "0", // 🔥 IMPORTANT (not marginLeft)
                  padding: "0",
                }}
              >
                {formatPoints(points)}
                {pointsLabel || "p"}
              </Link>
            )}
          </div>
        )}

        <Link
          to="/pricing"
          style={{
            ...navLinkStyle,
            color: COLORS.brandPurple,
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          {translations[currentLang].pricingNav}
        </Link>

        <LanguageSelector onLanguageChange={onLanguageChange} />

        {!user && (
          <div style={{ display: "inline-flex", minWidth: "fit-content" }}>
            <GoogleLoginButton />
          </div>
        )}

        {user && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              lineHeight: 1,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <img
                src={user.picture}
                alt="Profile"
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: `1px solid ${COLORS.brandTeal}`,
                }}
              />
              <span
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  color: COLORS.brandPurple,
                }}
              >
                {user.name || user.email}
              </span>
            </div>

            <span
              onClick={logout}
              style={{
                fontSize: "0.6rem",
                color: COLORS.brandTeal,
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: "50px",
              }}
            >
              {translations[currentLang].logout}
            </span>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
